import React, { useState, useEffect } from "react";
import { motion, useAnimation } from "framer-motion";
import { BsFillBagCheckFill } from "react-icons/bs";
import { IoBagCheck } from "react-icons/io5";
import Icon from "../common/Icon";
import axios from "../../config/axios";
import Modal from "../common/Modal";
import { emailSchema } from "../utils/validations";
import { Formik } from "formik";
import CMSFormInput from "../formFields/CMSFormInput";
import GeneralButton from "../common/GeneralButton";
import { useToast } from "../contexts/ToastContext";
import { useGeneral } from "../contexts/GeneralContext";
import { useAuth } from "../contexts/AuthContext";
import { useCart } from "../contexts/CartContext";
import { FaCartArrowDown } from "react-icons/fa";

const AddToCartButton = ({
  isOutOfStock,
  addedToCart,
  productId,
  quantity = 1,
  customerId,
  customerEmail,
  className,
  onSuccess,
  variant = "icon", // "icon" or "button"
  animationType = "slideRight", // "slideRight", "bounce", "pulse", "flip", "zoom"
}) => {
  const [added, setAdded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [email, setEmail] = useState("");
  const controls = useAnimation();
  const { addToast } = useToast();
  const { isloggedIn } = useAuth();
  const { showCart, refreshCart } = useGeneral();
  const { fetchCart } = useCart();

  useEffect(() => {
    if (customerEmail) {
      setEmail(customerEmail);
    } else {
      const storedEmail = localStorage.getItem("customerEmail");
      if (storedEmail) {
        setEmail(storedEmail);
      }
    }
  }, [customerEmail]);

  const buttonVariants = {
    slideRight: {
      x: [0, 100, -20, 0], // smoother motion with a subtle overshoot
      scale: [1, 1.15, 1.05, 1],
      rotate: [0, -8, 4, 0],
      opacity: [1, 0.95, 1],
      backgroundColor: ["#295B24", "#3EA03B", "#43B047", "#295B24"], // richer green tones
      color: ["#FFFFFF", "#FFFFFF", "#FFFFFF", "#FFFFFF"],
      transition: {
        duration: 0.9,
        ease: [0.25, 0.8, 0.25, 1], // custom cubic-bezier for bounce-in-out feel
        times: [0, 0.4, 0.7, 1],
      },
    },

    bounce: {
      scale: [1, 0.9, 1.1, 1],
      rotate: [0, -5, 5, 0],
      backgroundColor: ["#295B24", "#45a049", "#295B24"],
      transition: {
        duration: 0.6,
        ease: "easeInOut",
        times: [0, 0.3, 0.7, 1],
      },
    },
    pulse: {
      scale: [1, 1.05, 0.95, 1],
      backgroundColor: ["#295B24", "#45a049", "#295B24"],
      boxShadow: [
        "0 0 0 0 rgba(76,175,80,0.4)",
        "0 0 0 10px rgba(76,175,80,0)",
        "0 0 0 0 rgba(76,175,80,0)",
      ],
      transition: {
        duration: 1.5,
        ease: "easeInOut",
        repeat: Infinity,
        times: [0, 0.3, 0.7, 1],
      },
    },
    flip: {
      rotateY: [0, 180, 360],
      scale: [1, 1.1, 1],
      backgroundColor: ["#295B24", "#45a049", "#295B24"],
      transition: {
        duration: 0.8,
        ease: "easeInOut",
        times: [0, 0.5, 1],
      },
    },
    zoom: {
      scale: [1, 1.2, 1.1, 1],
      rotate: [0, -5, 5, 0],
      backgroundColor: ["#295B24", "#45a049", "#295B24"],
      transition: {
        duration: 0.7,
        ease: "easeInOut",
        times: [0, 0.3, 0.7, 1],
      },
    },
  };
  const handleConfirmEmail = async (email2) => {
  
    if (email2.trim()) {
      localStorage.setItem("customerEmail", email2);
      setEmail(email2);
      setShowModal(false);

      // Wait for modal to close and state to update
      setTimeout(() => {
        handleAddToCart({ stopPropagation: () => {} });
      }, 300);
    }
  };
  const handleAddToCart = async (e) => {
    e.stopPropagation();

    const storedEmail = localStorage.getItem("customerEmail");
    if (added || isOutOfStock) return;
    if (!email && !storedEmail) {
      setShowModal(true);
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post("/cart/add-product-to-cart", {
        productId,
        quantity,
        customerId: customerId || "",
        customerEmail: storedEmail || email,
      });

      if (response.status === 200 || response.status === 201) {
        const cartId = response.data?.cartId || response.data?.cart?._id;
        if (cartId) {
          localStorage.setItem("cartId", cartId);
        }

        if (variant === "icon") {
          await controls.start({
            y: [0, -40, 0],
            scale: [1, 1.2, 1],
            rotate: [0, -10, 0],
            zIndex: [100, 50, 1],
            opacity: [1, 0.8, 1],
            transition: {
              duration: 0.6,
              ease: [0.17, 0.67, 0.83, 0.67],
              times: [0, 0.5, 1],
            },
          });
        } else if (variant === "button") {
          console.log("");
        }

        setAdded(true);
        showCart();
        refreshCart();
        if (!isloggedIn) fetchCart();
        if (onSuccess) onSuccess(response.data);
      } else {
        addToast("Something went wrong", "error");
        await controls.start({
          x: [0, -10, 10, -10, 10, 0],
          y: [0, -5, 5, -5, 5, 0],
          rotate: [-2, 2, -2, 2, -2],
          transition: { duration: 0.4 },
        });
      }
    } catch (err) {
      console.error("Error adding to cart:", err);
      addToast("Something went wrong", "error");
      await controls.start({
        x: [0, -10, 10, -10, 10, 0],
        y: [0, -5, 5, -5, 5, 0],
        rotate: [-2, 2, -2, 2, -2],
        transition: { duration: 0.4 },
      });
    } finally {
      setLoading(false);
    }
  };

  if (isOutOfStock === "1") return null;

  if (variant === "icon") {
    return (
      <>
        <div className={className || "absolute right-4 bottom-4 z-[10]"}>
          {!addedToCart || addedToCart === 0 ? (
            <div>
              {!added ? (
                <motion.div
                  className={
                    className ||
                    "absolute bottom-[5px] right-[6px] z-[20] text-green hover:text-orange cursor-pointer"
                  }
                  title={loading ? "Adding..." : "Click to add product to cart"}
                  animate={controls}
                  initial={{ rotate: 0, scale: 1 }}
                  onClick={(e) => handleAddToCart(e)}
                >
                  <Icon icon="basket" size={40} />
                </motion.div>
              ) : (
                <motion.div
                  className="text-green"
                  title="Product added to cart"
                  initial={{ x: 50, opacity: 0, scale: 0 }}
                  animate={{
                    x: 0,
                    opacity: 1,
                    scale: 1,
                    rotate: [0, -15, 15, -10, 10, 0],
                  }}
                  transition={{
                    duration: 0.8,
                    ease: "easeOut",
                  }}
                >
                  <IoBagCheck size={40} />
                </motion.div>
              )}
            </div>
          ) : (
            addedToCart === 1 && (
              <div
                className="text-green"
                title="Product added to cart"
                initial={{ x: 50, opacity: 0, scale: 0 }}
                animate={{
                  x: 0,
                  opacity: 1,
                  scale: 1,
                  rotate: [0, -15, 15, -10, 10, 0],
                }}
                transition={{
                  duration: 0.8,
                  ease: "easeOut",
                }}
              >
                <IoBagCheck size={40} />
              </div>
            )
          )}
        </div>
        {showModal && (
          <EmailModal
            showModal={showModal}
            setShowModal={setShowModal}
            email={email}
            setEmail={setEmail}
            handleConfirmEmail={handleConfirmEmail}
          />
        )}
      </>
    );
  }

  return (
    <>
      <motion.button
        className={`flex items-center gap-2  py-1 rounded-[8px] text-nowrap ${
          added || addedToCart === 1 ? "px-0" : "px-4"
        } ${loading ? "opacity-70 cursor-wait" : "cursor-pointer"} ${
          className || ""
        }`}
        onClick={(e) => handleAddToCart(e)}
        disabled={loading || added || addedToCart === 1}
        initial={false}
        animate={{
          backgroundColor:
            added || addedToCart === 1 ? "rgba(0, 0, 0, 0)" : "#295B24", // your green
          color: added || addedToCart === 1 ? "#A0AE3F" : "#FFFFFF",
          transition: {
            backgroundColor: { duration: 0.6, ease: "easeInOut" },
            color: { duration: 0.4, ease: "easeInOut" },
          },
        }}
        whileHover={
          !(added || addedToCart === 1)
            ? {
                backgroundColor: "#3E8E41", // hover shade
                transition: { duration: 0.3, ease: "easeOut" },
              }
            : {}
        }
      >
        <div className="flex items-center gap-2 font-mono text-24pt">
          <motion.span
            animate={{
              opacity: [0.9, 1],
              transition: { duration: 0.4, ease: "easeInOut" },
            }}
          >
            {added || addedToCart === 1 ? "Added to cart" : "Add to cart"}
          </motion.span>

          <motion.div
            key={added || addedToCart === 1 ? "added" : "not-added"}
            initial={{ y: 0, rotate: 0, scale: 1, color: "#FFFFFF" }}
            animate={
              added || addedToCart === 1
                ? {
                    y: [0, -35, 0],
                    color: ["#FFFFFF", "#A0AE3F", "#A0AE3F"],
                    rotate: [0, -15, 10, -8, 6, -3, 0],
                    scale: [1, 1.15, 1],
                    transition: {
                      duration: 1,
                      ease: "easeInOut",
                      times: [0, 0.3, 0.6, 0.8, 0.9, 1],
                    },
                  }
                : {
                    y: [0],
                    rotate: 0,
                    scale: 1,
                    color: "#FFFFFF",
                    transition: { duration: 0.4 },
                  }
            }
          >
            {added || addedToCart === 1 ? (
              <IoBagCheck size={25} />
            ) : (
              <FaCartArrowDown size={20} />
            )}
          </motion.div>
        </div>
      </motion.button>

      {showModal && (
        <EmailModal
          showModal={showModal}
          setShowModal={setShowModal}
          email={email}
          setEmail={setEmail}
          handleConfirmEmail={handleConfirmEmail}
        />
      )}
    </>
  );
};
// Email Modal component
const EmailModal = ({
  showModal,
  setShowModal,
  email,
  setEmail,
  handleConfirmEmail,
}) => (
  <Modal
    isOpen={showModal}
    onClose={() => setShowModal(false)}
    title=""
    showCancel={false}
    showConfirm={false}
  >
    <p className="mb-1 text-left text-green text-lg ms-1">
      Kindly provide us your active email id to continue.{" "}
      <span className="text-20pt text-orange">*</span>
    </p>
    <Formik
      initialValues={{ email: email }}
      validationSchema={emailSchema}
      onSubmit={(values) => {
        setEmail(values?.email);
       

        handleConfirmEmail(values?.email);
      }}
    >
      {(form) => (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit(e);
          }}
          className="grid grid-cols-1 gap-6"
        >
          <CMSFormInput name="email" className="1" form={form} required />
          <div className="flex justify-end">
            <GeneralButton
              text="Submit"
              title="Click to submit"
              type="submit"
              className="text-20pt"
            />
          </div>
        </form>
      )}
    </Formik>
  </Modal>
);

export default AddToCartButton;
